import { Schema } from "effect";
import { ApiErrorSchema } from "./api";
import { DurableSubmittedUserMessageSchema } from "./agent-storage";
import {
  CanonicalIdentifierSchema,
  NonNegativeSafeIntegerSchema,
  PapersFlowBoundaryParseOptions,
  toPapersFlowStandardSchema,
} from "./standard";
import { utf8ByteLength } from "./utf8";
import { WorkspaceRoleSchema } from "./workspace";

export const AGENT_DELEGATION_CONTEXT_MAX_BYTES = 64 * 1_024;
export const AGENT_RESEARCH_RESULT_MAX_BYTES = 64 * 1_024;
export const AGENT_SYNTHESIS_PACKET_MAX_BYTES = 448 * 1_024;

const IdSchema = CanonicalIdentifierSchema;
const PositiveSafeIntegerSchema = NonNegativeSafeIntegerSchema.check(
  Schema.isGreaterThanOrEqualTo(1),
);
const ShortTextSchema = Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(500));
const ParagraphSchema = Schema.Trim.check(Schema.isMinLength(1), Schema.isMaxLength(4_000));
const BoundedJsonSchema = (maximumBytes: number, label: string) =>
  Schema.String.check(
    Schema.isMinLength(2),
    Schema.makeFilter(
      (value) =>
        utf8ByteLength(value) <= maximumBytes ||
        `${label} must not exceed ${maximumBytes} UTF-8 bytes`,
    ),
    Schema.makeFilter((value) => {
      try {
        JSON.parse(value);
        return true;
      } catch {
        return `${label} must be valid JSON`;
      }
    }),
  );

export const AgentDelegationBudgetSchema = Schema.Struct({
  maxSteps: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 8 })),
  maxTokens: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 64_000 })),
  maxWallTimeMs: Schema.Int.check(Schema.isBetween({ minimum: 1_000, maximum: 600_000 })),
  maxOutputBytes: Schema.Int.check(
    Schema.isBetween({ minimum: 1_024, maximum: AGENT_RESEARCH_RESULT_MAX_BYTES }),
  ),
  providerCostMicros: NonNegativeSafeIntegerSchema,
});

export const AgentAcceptanceCriterionSchema = Schema.Struct({
  criterionId: IdSchema,
  description: ShortTextSchema,
  classification: Schema.Literals(["mechanical", "semantic"]),
});

export const AgentAcceptanceCriteriaSchema = Schema.Array(AgentAcceptanceCriterionSchema).check(
  Schema.isMinLength(1),
  Schema.isMaxLength(16),
  Schema.makeFilter((criteria) => {
    const ids = criteria.map((criterion) => criterion.criterionId);
    return new Set(ids).size === ids.length || "acceptance criterion IDs must be unique";
  }),
);

export const DelegateWorkToolInputSchema = Schema.Struct({
  objective: ParagraphSchema,
  acceptanceCriteria: AgentAcceptanceCriteriaSchema,
  contextEntryIds: Schema.Array(IdSchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(16),
    Schema.makeFilter(
      (entryIds) =>
        new Set(entryIds).size === entryIds.length || "context entry IDs must be unique",
    ),
  ),
});

export const DelegateWorkToolInputStandardSchema = toPapersFlowStandardSchema(
  DelegateWorkToolInputSchema,
);

export const DelegateWorkToolResultSchema = Schema.Struct({
  status: Schema.Literal("scheduled"),
  childNodeId: IdSchema,
  childAgentId: IdSchema,
  queueReason: Schema.Literal("waiting-for-root-suspension"),
});

export const DelegateWorkToolOutputSchema = Schema.Union([
  Schema.Struct({ status: Schema.Literal("scheduling") }),
  DelegateWorkToolResultSchema,
]);

export const DelegateWorkToolOutputStandardSchema = toPapersFlowStandardSchema(
  DelegateWorkToolOutputSchema,
);

const ProjectFileContextEntrySchema = Schema.Struct({
  entryId: IdSchema,
  kind: Schema.Literal("project-file"),
  fileId: IdSchema,
  fileRevisionId: IdSchema,
  label: ShortTextSchema,
});

export const AgentDelegationContextEntrySchema = ProjectFileContextEntrySchema;

export const AgentDelegationContextManifestSchema = Schema.Struct({
  version: Schema.Literal(1),
  entries: Schema.Array(AgentDelegationContextEntrySchema).check(
    Schema.isMinLength(1),
    Schema.isMaxLength(16),
  ),
}).check(
  Schema.makeFilter((manifest) => {
    const ids = manifest.entries.map((entry) => entry.entryId);
    return new Set(ids).size === ids.length || "context entry IDs must be unique";
  }),
);

const AttemptFenceFields = {
  workspaceId: IdSchema,
  runId: IdSchema,
  attemptId: IdSchema,
  runGeneration: PositiveSafeIntegerSchema,
  attemptGeneration: PositiveSafeIntegerSchema,
  leaseId: IdSchema,
  leaseOwner: IdSchema,
} as const;

const CoordinatorTime = { now: NonNegativeSafeIntegerSchema } as const;

export const AcceptAgentDelegationInputSchema = Schema.Struct({
  ...AttemptFenceFields,
  delegationId: IdSchema,
  objective: ParagraphSchema,
  acceptanceCriteria: AgentAcceptanceCriteriaSchema,
  profile: Schema.Literal("research-readonly"),
  outputContract: Schema.Literal("research-findings/v1"),
  mergeStrategy: Schema.Literal("synthesize"),
  contextManifest: AgentDelegationContextManifestSchema,
  childBudget: AgentDelegationBudgetSchema,
  synthesisBudget: AgentDelegationBudgetSchema,
});

export const AcceptAgentDelegationRequestSchema = Schema.Struct({
  ...AcceptAgentDelegationInputSchema.fields,
  ...CoordinatorTime,
});

export const AcceptAgentDelegationResultSchema = Schema.Struct({
  status: Schema.Literal("scheduled"),
  childNodeId: IdSchema,
  childAgentId: IdSchema,
  childAttemptId: IdSchema,
  reservedProviderCostMicros: NonNegativeSafeIntegerSchema,
  queueReason: Schema.Literal("waiting-for-root-suspension"),
});

export const SuspendAgentAttemptForChildInputSchema = Schema.Struct({
  ...AttemptFenceFields,
  childNodeId: IdSchema,
});

export const SuspendAgentAttemptForChildRequestSchema = Schema.Struct({
  ...SuspendAgentAttemptForChildInputSchema.fields,
  ...CoordinatorTime,
});

export const SuspendAgentAttemptForChildResultSchema = Schema.Struct({
  status: Schema.Literal("waiting-on-child"),
  childNodeId: IdSchema,
  childAttemptId: IdSchema,
  runGeneration: PositiveSafeIntegerSchema,
});

export const ClaimNextAgentAttemptInputSchema = Schema.Struct({
  leaseId: IdSchema,
  leaseOwner: IdSchema,
  leaseDurationMs: Schema.Int.check(Schema.isBetween({ minimum: 1, maximum: 120_000 })),
});

export const ClaimNextAgentAttemptRequestSchema = Schema.Struct({
  ...ClaimNextAgentAttemptInputSchema.fields,
  ...CoordinatorTime,
});

const ClaimedAgentAttemptFields = {
  status: Schema.Literal("claimed"),
  workspaceId: IdSchema,
  projectId: IdSchema,
  threadId: IdSchema,
  runId: IdSchema,
  nodeId: IdSchema,
  agentId: IdSchema,
  attemptId: IdSchema,
  runGeneration: PositiveSafeIntegerSchema,
  attemptGeneration: PositiveSafeIntegerSchema,
  leaseId: IdSchema,
  leaseOwner: IdSchema,
  leaseExpiresAt: NonNegativeSafeIntegerSchema,
  provider: IdSchema,
  model: IdSchema,
  actorUserId: IdSchema,
  actorRole: WorkspaceRoleSchema,
  objective: ParagraphSchema,
  acceptanceCriteria: AgentAcceptanceCriteriaSchema,
  outputContract: Schema.Literal("research-findings/v1"),
  encodedContextManifest: BoundedJsonSchema(
    AGENT_DELEGATION_CONTEXT_MAX_BYTES,
    "encoded context manifest",
  ),
  budget: AgentDelegationBudgetSchema,
} as const;

const ClaimedAgentAttemptSchema = Schema.Union([
  Schema.Struct({
    ...ClaimedAgentAttemptFields,
    kind: Schema.Literal("child"),
    profile: Schema.Literal("research-readonly"),
  }),
  Schema.Struct({
    ...ClaimedAgentAttemptFields,
    kind: Schema.Literal("synthesis"),
    profile: Schema.Literal("root-synthesis"),
    encodedSynthesisPacket: BoundedJsonSchema(
      AGENT_SYNTHESIS_PACKET_MAX_BYTES,
      "encoded synthesis packet",
    ),
  }),
]);

export const ClaimNextAgentAttemptResultSchema = Schema.Union([
  Schema.Struct({ status: Schema.Literal("idle") }),
  ClaimedAgentAttemptSchema,
]);

const PageLocatorSchema = Schema.Struct({
  kind: Schema.Literal("page"),
  page: PositiveSafeIntegerSchema,
});
const SlideLocatorSchema = Schema.Struct({
  kind: Schema.Literal("slide"),
  slide: PositiveSafeIntegerSchema,
  elementId: Schema.optionalKey(IdSchema),
});
const LineRangeLocatorSchema = Schema.Struct({
  kind: Schema.Literal("line-range"),
  pathRefId: IdSchema,
  start: PositiveSafeIntegerSchema,
  end: PositiveSafeIntegerSchema,
}).check(
  Schema.makeFilter((locator) => locator.end >= locator.start || "line range end precedes start"),
);
const SheetRangeLocatorSchema = Schema.Struct({
  kind: Schema.Literal("sheet-range"),
  sheetId: IdSchema,
  range: ShortTextSchema,
});
const ArtifactElementLocatorSchema = Schema.Struct({
  kind: Schema.Literal("artifact-element"),
  revisionId: IdSchema,
  elementId: IdSchema,
});
const ArtifactNodeLocatorSchema = Schema.Struct({
  kind: Schema.Literal("artifact-node"),
  revisionId: IdSchema,
  nodeId: IdSchema,
  characterStart: Schema.optionalKey(NonNegativeSafeIntegerSchema),
  characterEnd: Schema.optionalKey(NonNegativeSafeIntegerSchema),
}).check(
  Schema.makeFilter(
    (locator) =>
      locator.characterStart === undefined ||
      locator.characterEnd === undefined ||
      locator.characterEnd >= locator.characterStart ||
      "artifact character range end precedes start",
  ),
);
const WebSectionLocatorSchema = Schema.Struct({
  kind: Schema.Literal("web-section"),
  retrievalId: IdSchema,
  heading: Schema.optionalKey(ShortTextSchema),
});
const ExternalItemLocatorSchema = Schema.Struct({
  kind: Schema.Literal("external-item"),
  externalItemRefId: IdSchema,
});

export const ResearchEvidenceLocatorV1Schema = Schema.Union([
  PageLocatorSchema,
  SlideLocatorSchema,
  LineRangeLocatorSchema,
  SheetRangeLocatorSchema,
  ArtifactElementLocatorSchema,
  ArtifactNodeLocatorSchema,
  WebSectionLocatorSchema,
  ExternalItemLocatorSchema,
]);

export const ResearchEvidenceV1Schema = Schema.Struct({
  evidenceId: IdSchema,
  contextEntryId: IdSchema,
  sourceRefId: IdSchema,
  relation: Schema.Literals(["supports", "contradicts"]),
  locator: ResearchEvidenceLocatorV1Schema,
});

export const ResearchFindingV1Schema = Schema.Struct({
  findingId: IdSchema,
  kind: Schema.Literals(["fact", "inference", "disagreement"]),
  statement: ParagraphSchema,
  confidence: Schema.Literals(["high", "medium", "low"]),
  criterionIds: Schema.Array(IdSchema).check(Schema.isMaxLength(16)),
  evidence: Schema.Array(ResearchEvidenceV1Schema).check(Schema.isMaxLength(8)),
}).check(
  Schema.makeFilter((finding) => {
    const criterionIds = new Set(finding.criterionIds);
    return (
      criterionIds.size === finding.criterionIds.length || "finding criterion IDs must be unique"
    );
  }),
  Schema.makeFilter((finding) => {
    const evidenceIds = finding.evidence.map((evidence) => evidence.evidenceId);
    return (
      new Set(evidenceIds).size === evidenceIds.length || "finding evidence IDs must be unique"
    );
  }),
  Schema.makeFilter((finding) => {
    if (finding.kind === "disagreement") {
      return (
        new Set(finding.evidence.map((evidence) => evidence.sourceRefId)).size >= 2 ||
        "a disagreement requires at least two distinct sources"
      );
    }
    return finding.evidence.length > 0 || "a fact or inference requires evidence";
  }),
);

export const ResearchGapV1Schema = Schema.Struct({
  criterionId: Schema.optionalKey(IdSchema),
  reason: Schema.Literals([
    "no-evidence",
    "conflicting-sources",
    "access-denied",
    "budget-exhausted",
  ]),
  description: ParagraphSchema,
  recommendedNextStep: ParagraphSchema,
});

export const ResearchCriterionCoverageV1Schema = Schema.Struct({
  criterionId: IdSchema,
  assessment: Schema.Literals(["addressed", "partially-addressed", "not-addressed"]),
  findingIds: Schema.Array(IdSchema).check(Schema.isMaxLength(32)),
  explanation: ParagraphSchema,
}).check(
  Schema.makeFilter((coverage) =>
    coverage.assessment === "not-addressed"
      ? coverage.findingIds.length === 0 || "not-addressed coverage cannot cite findings"
      : coverage.findingIds.length > 0 || `${coverage.assessment} coverage requires a finding`,
  ),
);

export const ResearchFindingsV1Schema = Schema.Struct({
  contract: Schema.Literal("research-findings/v1"),
  summary: ParagraphSchema,
  findings: Schema.Array(ResearchFindingV1Schema).check(Schema.isMaxLength(32)),
  coverage: Schema.Array(ResearchCriterionCoverageV1Schema).check(Schema.isMaxLength(16)),
  gaps: Schema.Array(ResearchGapV1Schema).check(Schema.isMaxLength(16)),
}).check(
  Schema.makeFilter((result) => {
    const findingIds = result.findings.map((finding) => finding.findingId);
    return new Set(findingIds).size === findingIds.length || "finding IDs must be unique";
  }),
  Schema.makeFilter((result) => {
    const coverageIds = result.coverage.map((coverage) => coverage.criterionId);
    return (
      new Set(coverageIds).size === coverageIds.length || "coverage criterion IDs must be unique"
    );
  }),
);

const ResearchCompletionV1Schema = Schema.Union([
  Schema.Struct({ status: Schema.Literal("succeeded"), content: ResearchFindingsV1Schema }),
  Schema.Struct({
    status: Schema.Literal("partial"),
    content: ResearchFindingsV1Schema,
    unmetCriterionIds: Schema.Array(IdSchema).check(Schema.isMinLength(1), Schema.isMaxLength(16)),
  }),
  Schema.Struct({
    status: Schema.Literal("blocked"),
    blocker: Schema.Struct({
      code: Schema.Literals([
        "capability-unavailable",
        "access-denied",
        "dependency-unavailable",
        "user-decision-required",
        "budget-exhausted",
      ]),
      description: ParagraphSchema,
      recommendedNextStep: ParagraphSchema,
    }),
    partialContent: Schema.optionalKey(ResearchFindingsV1Schema),
  }),
]);

export const AgentResultHandoffV1Schema = Schema.Struct({
  summary: ParagraphSchema,
  sourceRefIds: Schema.Array(IdSchema).check(Schema.isMaxLength(64)),
  artifactRefIds: Schema.Array(IdSchema).check(Schema.isMaxLength(32)),
  uncertainties: Schema.Array(ShortTextSchema).check(Schema.isMaxLength(16)),
  blockers: Schema.Array(ShortTextSchema).check(Schema.isMaxLength(16)),
  recommendedNextAction: ParagraphSchema,
});

export const AgentResultAccountingRefsV1Schema = Schema.Struct({
  stepCount: NonNegativeSafeIntegerSchema,
  toolCallCount: NonNegativeSafeIntegerSchema,
  usageEventIds: Schema.Array(IdSchema).check(Schema.isMaxLength(128)),
  providerCostEntryIds: Schema.Array(IdSchema).check(Schema.isMaxLength(128)),
  billableCostEntryIds: Schema.Array(IdSchema).check(Schema.isMaxLength(128)),
});

export const ResearchResultModelOutputV1Schema = Schema.Struct({
  completion: ResearchCompletionV1Schema,
  handoff: AgentResultHandoffV1Schema,
});

export const ResearchResultModelOutputV1StandardSchema = toPapersFlowStandardSchema(
  ResearchResultModelOutputV1Schema,
);

export const ResearchResultSubmissionV1Schema = Schema.Struct({
  ...ResearchResultModelOutputV1Schema.fields,
  accounting: AgentResultAccountingRefsV1Schema,
});

export const ResearchResultSubmissionV1StandardSchema = toPapersFlowStandardSchema(
  ResearchResultSubmissionV1Schema,
);

export const ResearchResultEnvelopeV1Schema = Schema.Struct({
  identity: Schema.Struct({
    runId: IdSchema,
    nodeId: IdSchema,
    attemptId: IdSchema,
    attemptGeneration: PositiveSafeIntegerSchema,
    outputContract: Schema.Literal("research-findings/v1"),
  }),
  completion: Schema.Union([
    ResearchCompletionV1Schema,
    Schema.Struct({ status: Schema.Literal("failed"), error: ApiErrorSchema }),
    Schema.Struct({
      status: Schema.Literal("cancelled"),
      reason: Schema.Literals(["child-stop", "run-stop", "superseded", "policy-revoked"]),
    }),
  ]),
  handoff: AgentResultHandoffV1Schema,
  accounting: AgentResultAccountingRefsV1Schema,
});

export const AgentSynthesisPacketV1Schema = Schema.Struct({
  version: Schema.Literal(1),
  originalRequest: DurableSubmittedUserMessageSchema,
  delegation: Schema.Struct({
    objective: ParagraphSchema,
    acceptanceCriteria: AgentAcceptanceCriteriaSchema,
    contextManifest: AgentDelegationContextManifestSchema,
  }),
  result: ResearchResultEnvelopeV1Schema,
});

export const CommitChildAgentResultInputSchema = Schema.Struct({
  ...AttemptFenceFields,
  encodedSubmission: BoundedJsonSchema(
    AGENT_RESEARCH_RESULT_MAX_BYTES,
    "encoded research result submission",
  ),
});

export const CommitChildAgentResultRequestSchema = Schema.Struct({
  ...CommitChildAgentResultInputSchema.fields,
  ...CoordinatorTime,
});

export const CommitChildAgentResultResultSchema = Schema.Struct({
  status: Schema.Literal("synthesis-scheduled"),
  childNodeId: IdSchema,
  resultId: IdSchema,
  synthesisAttemptId: IdSchema,
  runGeneration: PositiveSafeIntegerSchema,
});

export const decodeAgentDelegationContextManifest = Schema.decodeUnknownSync(
  AgentDelegationContextManifestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentAcceptanceCriteria = Schema.decodeUnknownSync(
  AgentAcceptanceCriteriaSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeAgentAcceptanceCriteria = Schema.encodeSync(
  AgentAcceptanceCriteriaSchema,
  PapersFlowBoundaryParseOptions,
);
export const areAgentAcceptanceCriteriaEquivalent = Schema.toEquivalence(
  AgentAcceptanceCriteriaSchema,
);
export const areAgentDelegationContextManifestsEquivalent = Schema.toEquivalence(
  AgentDelegationContextManifestSchema,
);
export const areResearchResultEnvelopesEquivalent = Schema.toEquivalence(
  ResearchResultEnvelopeV1Schema,
);
export const encodeAgentDelegationContextManifest = Schema.encodeSync(
  AgentDelegationContextManifestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeResearchResultSubmissionV1 = Schema.decodeUnknownSync(
  ResearchResultSubmissionV1Schema,
  PapersFlowBoundaryParseOptions,
);
export const encodeResearchResultSubmissionV1 = Schema.encodeSync(
  ResearchResultSubmissionV1Schema,
  PapersFlowBoundaryParseOptions,
);
export const decodeResearchResultEnvelopeV1 = Schema.decodeUnknownSync(
  ResearchResultEnvelopeV1Schema,
  PapersFlowBoundaryParseOptions,
);
export const encodeResearchResultEnvelopeV1 = Schema.encodeSync(
  ResearchResultEnvelopeV1Schema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAgentSynthesisPacketV1 = Schema.decodeUnknownSync(
  AgentSynthesisPacketV1Schema,
  PapersFlowBoundaryParseOptions,
);
export const encodeAgentSynthesisPacketV1 = Schema.encodeSync(
  AgentSynthesisPacketV1Schema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAcceptAgentDelegationRequest = Schema.decodeUnknownSync(
  AcceptAgentDelegationRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeAcceptAgentDelegationRequest = Schema.encodeSync(
  AcceptAgentDelegationRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeAcceptAgentDelegationResult = Schema.decodeUnknownSync(
  AcceptAgentDelegationResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeSuspendAgentAttemptForChildRequest = Schema.decodeUnknownSync(
  SuspendAgentAttemptForChildRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeSuspendAgentAttemptForChildRequest = Schema.encodeSync(
  SuspendAgentAttemptForChildRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeSuspendAgentAttemptForChildResult = Schema.decodeUnknownSync(
  SuspendAgentAttemptForChildResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeClaimNextAgentAttemptRequest = Schema.decodeUnknownSync(
  ClaimNextAgentAttemptRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeClaimNextAgentAttemptRequest = Schema.encodeSync(
  ClaimNextAgentAttemptRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeClaimNextAgentAttemptResult = Schema.decodeUnknownSync(
  ClaimNextAgentAttemptResultSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCommitChildAgentResultRequest = Schema.decodeUnknownSync(
  CommitChildAgentResultRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const encodeCommitChildAgentResultRequest = Schema.encodeSync(
  CommitChildAgentResultRequestSchema,
  PapersFlowBoundaryParseOptions,
);
export const decodeCommitChildAgentResultResult = Schema.decodeUnknownSync(
  CommitChildAgentResultResultSchema,
  PapersFlowBoundaryParseOptions,
);

export function encodeResearchResultSubmissionJson(submission: ResearchResultSubmissionV1): string {
  const encoded = JSON.stringify(encodeResearchResultSubmissionV1(submission));
  if (utf8ByteLength(encoded) > AGENT_RESEARCH_RESULT_MAX_BYTES) {
    throw new Error("Research result submission exceeds its persistence limit");
  }
  return encoded;
}

export function encodeAgentSynthesisPacketJson(packet: AgentSynthesisPacketV1): string {
  const encoded = JSON.stringify(encodeAgentSynthesisPacketV1(packet));
  if (utf8ByteLength(encoded) > AGENT_SYNTHESIS_PACKET_MAX_BYTES) {
    throw new Error("Agent synthesis packet exceeds its persistence limit");
  }
  return encoded;
}

export type AgentDelegationBudget = typeof AgentDelegationBudgetSchema.Type;
export type AgentAcceptanceCriterion = typeof AgentAcceptanceCriterionSchema.Type;
export type AgentAcceptanceCriteria = typeof AgentAcceptanceCriteriaSchema.Type;
export type AgentDelegationContextManifest = typeof AgentDelegationContextManifestSchema.Type;
export type AgentDelegationContextEntry = typeof AgentDelegationContextEntrySchema.Type;
export type DelegateWorkToolInput = typeof DelegateWorkToolInputSchema.Type;
export type DelegateWorkToolResult = typeof DelegateWorkToolResultSchema.Type;
export type DelegateWorkToolOutput = typeof DelegateWorkToolOutputSchema.Type;
export type AcceptAgentDelegationInput = typeof AcceptAgentDelegationInputSchema.Type;
export type AcceptAgentDelegationResult = typeof AcceptAgentDelegationResultSchema.Type;
export type SuspendAgentAttemptForChildInput = typeof SuspendAgentAttemptForChildInputSchema.Type;
export type SuspendAgentAttemptForChildResult = typeof SuspendAgentAttemptForChildResultSchema.Type;
export type ClaimNextAgentAttemptInput = typeof ClaimNextAgentAttemptInputSchema.Type;
export type ClaimNextAgentAttemptResult = typeof ClaimNextAgentAttemptResultSchema.Type;
export type ResearchFindingsV1 = typeof ResearchFindingsV1Schema.Type;
export type ResearchResultModelOutputV1 = typeof ResearchResultModelOutputV1Schema.Type;
export type ResearchResultSubmissionV1 = typeof ResearchResultSubmissionV1Schema.Type;
export type ResearchResultEnvelopeV1 = typeof ResearchResultEnvelopeV1Schema.Type;
export type AgentSynthesisPacketV1 = typeof AgentSynthesisPacketV1Schema.Type;
export type CommitChildAgentResultInput = typeof CommitChildAgentResultInputSchema.Type;
export type CommitChildAgentResultResult = typeof CommitChildAgentResultResultSchema.Type;
